/**
 * FeatureGate – gates content behind feature access check.
 * On mount: GET /api/platform/feature-check/?feature=X
 * If access: renders children. If no access: lock + tooltip + upgrade button.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Tooltip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { enhancedApi } from '../api';

const FeatureGate = ({ feature, children }) => {
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!feature) {
        setHasAccess(true);
        setLoading(false);
        return;
      }
      try {
        const res = await enhancedApi.checkFeatureAccess(feature);
        const data = res.data?.data ?? res.data;
        const allowed = data?.has_access ?? data?.allowed ?? false;
        if (!cancelled) setHasAccess(!!allowed);
      } catch {
        if (!cancelled) setHasAccess(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [feature]);

  if (loading) {
    return (
      <Box sx={{ opacity: 0.5, pointerEvents: 'none' }}>
        {children}
      </Box>
    );
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  return (
    <Tooltip title="Upgrade to unlock">
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <LockIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
        <Button
          size="small"
          variant="outlined"
          onClick={() => navigate('/billing')}
          sx={{ textTransform: 'none' }}
        >
          Upgrade
        </Button>
      </Box>
    </Tooltip>
  );
};

export default FeatureGate;
